/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import type { ReactNode } from 'react';
import { createElement } from 'react';
import {
  IconDashboard,
  IconChartBar,
  IconChartLine,
  IconTrendingUp,
  IconCube3dSphere,
  IconClipboardList,
  IconGauge,
  IconBell,
  IconBellCog,
  IconTopologyStar3,
  IconSettings2,
  IconEngine,
  IconExternalLink,
} from '../components/icons';
import { ROUTES } from './router';

export interface NavItem {
  labelKey: string;
  icon: ReactNode;
  enabled: boolean;
  to?: string;
}

export interface NavGroup {
  titleKey: string;
  items: NavItem[];
}

const ICON_SIZE = 18;
const icon = (Cmp: (props: { size?: number }) => ReactNode) => createElement(Cmp, { size: ICON_SIZE });

export function useNavGroups(): NavGroup[] {
  return [
    {
      titleKey: 'menu_groupAnalytics',
      items: [
        { labelKey: 'menu_dashboard', icon: icon(IconDashboard), enabled: true, to: ROUTES.dashboard },
        { labelKey: 'menu_energyConsumption', icon: icon(IconChartBar), enabled: false },
        { labelKey: 'menu_meterAnalytics', icon: icon(IconChartLine), enabled: true, to: ROUTES.meterAnalytics },
        { labelKey: 'menu_secAnalysis', icon: icon(IconTrendingUp), enabled: false },
        { labelKey: 'menu_facility', icon: icon(IconCube3dSphere), enabled: false },
      ],
    },
    {
      titleKey: 'menu_groupSec',
      items: [
        { labelKey: 'menu_secEquipmentCatalog', icon: icon(IconClipboardList), enabled: false },
        { labelKey: 'menu_productionDataEntry', icon: icon(IconGauge), enabled: false },
      ],
    },
    {
      titleKey: 'menu_groupAlerts',
      items: [
        { labelKey: 'menu_alerts', icon: icon(IconBell), enabled: false },
        { labelKey: 'menu_alertRuleConfiguration', icon: icon(IconBellCog), enabled: false },
      ],
    },
    {
      titleKey: 'menu_groupMeters',
      items: [
        { labelKey: 'menu_meterInventory', icon: icon(IconClipboardList), enabled: true, to: ROUTES.meterInventory },
        { labelKey: 'menu_facilityOverview', icon: icon(IconTopologyStar3), enabled: true, to: ROUTES.meterHierarchy },
        {
          labelKey: 'menu_meterConfiguration',
          icon: icon(IconSettings2),
          enabled: true,
          to: ROUTES.meterHierarchyConfiguration,
        },
        { labelKey: 'menu_equipment', icon: icon(IconEngine), enabled: true, to: ROUTES.equipment },
      ],
    },
    {
      titleKey: 'menu_groupExternal',
      items: [{ labelKey: 'menu_infra', icon: icon(IconExternalLink), enabled: false }],
    },
  ];
}
